/**
 * Централизованный слой тостов поверх `solid-toast`.
 *
 * Зачем не голый `solid-toast`:
 *  - **Единый вид ошибок по всему фронту.** `toast.error(...)` рендерит
 *    раскрываемую карточку `ErrorToast`: в свёрнутом виде — заголовок,
 *    по клику — полная информация (HTTP-код, пофилдовые ошибки валидации
 *    DRF, исходный текст) + кнопка «Скопировать».
 *  - **Пауза таймера при наведении.** Достаётся бесплатно: `ToastContainer`
 *    из solid-toast сам шлёт START_PAUSE/END_PAUSE на mouseenter/mouseleave,
 *    поэтому любой `toast.custom` останавливает отсчёт автодисмисса, пока
 *    курсор над тостом — успеваешь прочитать.
 *  - **Глобальный обработчик.** `notifyError` вызывается из QueryCache /
 *    MutationCache в `App.tsx` — ошибки запросов/мутаций всплывают сами.
 *
 * Модуль — drop-in замена `solid-toast`: экспортирует `toast` (named +
 * default) с той же сигнатурой и ре-экспортирует `Toaster`. Поэтому по коду
 * достаточно заменить импорт `'solid-toast'` → `'~/lib/toast'`.
 */
import { type Component, createSignal, For, Show } from 'solid-js'
import solidToast from 'solid-toast'
import type { Message, Renderable, Toast, ToastOptions } from 'solid-toast'
import { AlertCircle, Check, ChevronDown, ChevronUp, Copy, X } from 'lucide-solid'
import { useI18n } from '~/i18n'

export { Toaster } from 'solid-toast'

/** Длительность по умолчанию (мс). Чуть дольше дефолтных 4с — ошибки важнее. */
const ERROR_DURATION = 6000
const SUCCESS_DURATION = 3000

// ─────────────────────────── нормализация ошибки ───────────────────────────

interface ErrorData {
  /** Основное человекочитаемое сообщение. */
  title: string
  /** HTTP-код (если ошибка пришла из API). */
  status?: number
  /** Разобранное тело ответа — источник пофилдовых ошибок. */
  body?: unknown
}

/** Ключи тела ответа, которые НЕ являются именами полей (общие сообщения). */
const NON_FIELD_KEYS = new Set(['detail', 'error', 'message'])

/**
 * Приводит произвольный объект ошибки к `ErrorData`. Покрывает все формы,
 * долетающие до тоста: строку, `ApiError` (`{ detail, status, body }`),
 * `ApiRequestError` и обычный `Error`. `status`/`body` читаем и у Error-инстансов
 * (ApiRequestError — наследник Error), иначе для прямых вызовов apiRequest
 * детализация терялась бы.
 */
function normalizeError(input: unknown, fallback?: string): ErrorData {
  if (typeof input === 'string') {
    return { title: input || fallback || '' }
  }
  if (input && typeof input === 'object') {
    const o = input as Record<string, unknown>
    const detail = typeof o.detail === 'string' ? o.detail : undefined
    const msg = typeof o.message === 'string' ? o.message : undefined
    return {
      title: detail || msg || fallback || '',
      status: typeof o.status === 'number' && o.status > 0 ? o.status : undefined,
      body: o.body,
    }
  }
  return { title: fallback ?? String(input) }
}

/** Достаёт пофилдовые сообщения DRF из тела ответа (`{ field: [msg], ... }`). */
function extractFieldErrors(body: unknown): { field: string; text: string }[] {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return []
  }
  const out: { field: string; text: string }[] = []
  for (const [field, value] of Object.entries(body as Record<string, unknown>)) {
    if (NON_FIELD_KEYS.has(field)) {
      continue
    }
    let text = ''
    if (typeof value === 'string') {
      text = value
    } else if (Array.isArray(value)) {
      text = value.filter((v) => typeof v === 'string').join(' ')
    } else {
      continue
    }
    if (text) {
      out.push({ field, text })
    }
  }
  return out
}

/** Стабильный id из текста — одинаковые ошибки схлопываются в один тост (UPSERT). */
function hashId(s: string): string {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0
  }
  return `err:${Math.abs(h).toString(36)}`
}

// ─────────────────────────────── компоненты ───────────────────────────────

interface ErrorToastProps {
  toast: Toast
  data: ErrorData
}

const ErrorToast: Component<ErrorToastProps> = (props) => {
  const { t } = useI18n()
  const [expanded, setExpanded] = createSignal(false)
  const [copied, setCopied] = createSignal(false)

  const title = () => props.data.title || t('toast.errorTitle')
  const fields = () => extractFieldErrors(props.data.body)
  const hasDetail = () =>
    props.data.status !== undefined || fields().length > 0 || title().length > 90

  // Раскрытие — локальный сигнал. Фиксировать тост (duration→Infinity) не нужно:
  // при наведении solid-toast сам ставит таймер автодисмисса на паузу, а курсор
  // и так над тостом, раз по нему кликнули. Отвёл мышь — отсчёт продолжился.
  const toggle = () => {
    if (hasDetail()) {
      setExpanded((v) => !v)
    }
  }

  const copyText = () => {
    const parts = [title()]
    if (props.data.status !== undefined) {
      parts.push(`${t('toast.statusCode')}: ${props.data.status}`)
    }
    for (const f of fields()) {
      parts.push(`${f.field}: ${f.text}`)
    }
    void navigator.clipboard
      .writeText(parts.join('\n'))
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => undefined)
  }

  return (
    <div
      class="pointer-events-auto w-[360px] max-w-[calc(100vw-32px)] overflow-hidden rounded-lg border border-danger-soft bg-surface shadow-lg"
      role="alert"
    >
      <div class="flex items-start gap-2.5 p-3">
        <AlertCircle class="mt-0.5 h-4 w-4 shrink-0 text-danger" />
        <button
          type="button"
          onClick={toggle}
          class={`min-w-0 flex-1 text-left ${hasDetail() ? 'cursor-pointer' : 'cursor-default'}`}
          aria-expanded={expanded()}
        >
          <span
            class="block text-sm font-medium text-ink"
            classList={{ 'line-clamp-2': !expanded() }}
          >
            {title()}
          </span>
        </button>
        <div class="flex shrink-0 items-center gap-0.5">
          <Show when={hasDetail()}>
            <button
              type="button"
              onClick={toggle}
              class="rounded p-1 text-ink-muted hover:bg-surface-hover hover:text-ink"
              title={expanded() ? t('toast.hide') : t('toast.details')}
              aria-label={expanded() ? t('toast.hide') : t('toast.details')}
            >
              <Show when={expanded()} fallback={<ChevronDown class="h-3.5 w-3.5" />}>
                <ChevronUp class="h-3.5 w-3.5" />
              </Show>
            </button>
          </Show>
          <button
            type="button"
            onClick={() => solidToast.dismiss(props.toast.id)}
            class="rounded p-1 text-ink-muted hover:bg-surface-hover hover:text-ink"
            title={t('common.close')}
            aria-label={t('common.close')}
          >
            <X class="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <Show when={expanded()}>
        <div class="border-t border-border-faint bg-surface-2 px-3 py-2.5 text-xs text-ink-2">
          <Show when={props.data.status !== undefined}>
            <div class="mb-1.5 flex items-center gap-1.5">
              <span class="text-ink-muted">{t('toast.statusCode')}:</span>
              <span class="mono rounded bg-danger-soft px-1.5 py-0.5 font-medium text-danger-soft-ink">
                {props.data.status}
              </span>
            </div>
          </Show>
          <Show when={fields().length > 0}>
            <ul class="mb-1.5 space-y-1">
              <For each={fields()}>
                {(f) => (
                  <li class="flex gap-1.5">
                    <span class="mono shrink-0 text-ink-muted">{f.field}:</span>
                    <span class="min-w-0 break-words">{f.text}</span>
                  </li>
                )}
              </For>
            </ul>
          </Show>
          <button
            type="button"
            onClick={copyText}
            class="mt-0.5 inline-flex items-center gap-1 rounded px-1.5 py-1 text-ink-muted hover:bg-surface-hover hover:text-ink"
          >
            <Show when={copied()} fallback={<Copy class="h-3 w-3" />}>
              <Check class="h-3 w-3 text-success" />
            </Show>
            <span>{copied() ? t('common.copied') : t('common.copy')}</span>
          </button>
        </div>
      </Show>
    </div>
  )
}

const SuccessToast: Component<{ toast: Toast; message: Renderable }> = (props) => {
  return (
    <div
      class="pointer-events-auto flex w-[360px] max-w-[calc(100vw-32px)] items-start gap-2.5 rounded-lg border border-success-soft bg-surface p-3 shadow-lg"
      role="status"
    >
      <Check class="mt-0.5 h-4 w-4 shrink-0 text-success" />
      <div class="min-w-0 flex-1 text-sm text-ink">{props.message}</div>
    </div>
  )
}

// ─────────────────────────────── публичный API ──────────────────────────────

/**
 * Показать тост-ошибку. Принимает строку, `Error` или `ApiError`
 * (`{ detail, status, body }`). По клику раскрывает детали; таймер
 * автодисмисса встаёт на паузу при наведении.
 */
export function notifyError(input: unknown, fallback?: string, opts?: ToastOptions): string {
  const data = normalizeError(input, fallback)
  const id = opts?.id ?? hashId(data.title)
  const duration = opts?.duration ?? ERROR_DURATION
  return solidToast.custom((to) => <ErrorToast toast={to} data={data} />, {
    ...opts,
    id,
    duration,
  })
}

/** Показать тост-успех в едином стиле. */
export function notifySuccess(message: Renderable, opts?: ToastOptions): string {
  return solidToast.custom((to) => <SuccessToast toast={to} message={message} />, {
    duration: SUCCESS_DURATION,
    ...opts,
  })
}

/**
 * Drop-in замена `solid-toast`: те же `error`/`success`/`custom`/`dismiss`/…,
 * но `error`/`success` рендерят наши карточки.
 */
const toast = Object.assign((msg: Message, opts?: ToastOptions) => solidToast(msg, opts), {
  error: (msg: unknown, opts?: ToastOptions) => notifyError(msg, undefined, opts),
  success: (msg: Renderable, opts?: ToastOptions) => notifySuccess(msg, opts),
  loading: (msg: Message, opts?: ToastOptions) => solidToast.loading(msg, opts),
  custom: (msg: Message, opts?: ToastOptions) => solidToast.custom(msg, opts),
  dismiss: (id?: string) => solidToast.dismiss(id),
  remove: (id?: string) => solidToast.remove(id),
  promise: solidToast.promise,
})

export { toast }
export default toast

// ─────────────────────────── dev-хелпер для проверки ───────────────────────────
// Только в dev-сборке (vite сам выкидывает этот блок из прод-бандла).
// В консоли браузера: `__toastTest.error()` / `.success()` / `.simple()`.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  ;(window as unknown as Record<string, unknown>).__toastTest = {
    /** Богатая ошибка: HTTP-код + пофилдовые ошибки валидации (раскрывается). */
    error: () =>
      notifyError({
        detail: 'Не удалось сохранить контрагента',
        status: 422,
        body: {
          inn: ['Неверный формат ИНН — ожидается 10 или 12 цифр'],
          display_name: ['Это поле обязательно.'],
        },
      }),
    /** Простая ошибка-строка (без раскрытия, если текст короткий). */
    simple: (msg = 'Что-то пошло не так') => notifyError(msg),
    /** Успех. */
    success: (msg = 'Изменения сохранены') => notifySuccess(msg),
  }
}
